import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { StepContextProvider } from '@post-office/message-step-context';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--growth-starburst/jira-issue-view-twc-spotlight-tour';

import { useCallback, useContext } from 'react';

import { DEFAULT_TOUR } from '../../../components/constants';

import {
	TWCSpotlightCardTour,
	type TWCSpotlightCardTourDependencies,
} from '../../../components/twc-spotlight-card-tour';

import { type StepId, type MessageTemplateContext } from '../../../index';

import { SurveyContext } from '@post-office/spotlight-engagement-survey';

import { type SpotlightProps } from '@atlaskit/onboarding/types';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

type RenderStage = {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (
		input: PlacementRenderProps & {
			tour?: {
				steps: {
					stepId: StepId;
					targetId: string;
					dialogPlacement?: SpotlightProps['dialogPlacement'];
				}[];
				startFromStep?: number;
			};
		},
	) => JSX.Element | null;
};

export const createRender = (dependencies?: TWCSpotlightCardTourDependencies): RenderStage =>
	PlacementFrontend.createFrontendStage({
		type: 'render',
		validate: noValidation<RenderProps>(),
		Component: ({ tour = DEFAULT_TOUR, onDismiss }: RenderProps) => {
			const { messageDismissed } = useMessageEvent();
			const { onAcknowledged, onDeclined } = usePostOfficeUserInteractions();
			const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
			const { showSurvey } = useContext(SurveyContext);

			const handleTourComplete = useCallback(() => {
				void messageDismissed();
				void onAcknowledged({
					interactionIdentifier: 'tour-complete',
					additionalMetadata: { tourCompleted: true },
				});
				showSurvey?.();
				onDismiss?.();

				fireAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'twcSpotlightTourCompleteButton',
				});
			}, [messageDismissed, onAcknowledged, onDismiss, fireAnalyticsEvent]);

			const handleTourSkip = useCallback(() => {
				void messageDismissed();
				void onDeclined({
					interactionIdentifier: 'skip-tour',
					additionalMetadata: { tourSkipped: true },
				});
				showSurvey?.();
				onDismiss?.();

				fireAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'twcSpotlightTourSkipButton',
				});
			}, [messageDismissed, onDeclined, onDismiss, fireAnalyticsEvent]);

			if (!tour) {
				return null;
			}

			return (
				<StepContextProvider
					steps={tour.steps}
					initialStep={tour.startFromStep || 0}
					progressIndexOffset={1}
					onEnd={handleTourComplete}
				>
					<TWCSpotlightCardTour
						dependencies={dependencies}
						tour={tour}
						onTourSkip={handleTourSkip}
						onTourComplete={handleTourComplete}
					/>
				</StepContextProvider>
			);
		},
	});

export const render: RenderStage = createRender();

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
